import { Injectable, OnDestroy } from '@angular/core';

/**
 * Paints CB2 preview highlights (pick hover + scroll-to-bloc flash) as fixed
 * overlays on top of the page instead of promoting `.cb2-pick-bloc` wrappers
 * to real boxes. Wrappers must stay `display: contents` in every state so the
 * preview layout matches production exactly — a wrapper with a box would
 * become the grid/flex item itself and break layouts such as the Mosaic
 * block's `col-span` cards.
 */
@Injectable({ providedIn: 'root' })
export class Cb2BlocHighlightService implements OnDestroy {
  private hoverOverlay: HTMLElement | null = null;
  private hoverTarget: HTMLElement | null = null;
  private readonly repositionUnlisteners: Array<() => void> = [];

  ngOnDestroy(): void {
    this.reset();
  }

  /** Shows the hover tint over `target`, or hides it when `target` is null. */
  setHoverTarget(target: HTMLElement | null): void {
    if (target === this.hoverTarget) {
      return;
    }
    this.hoverTarget = target;

    if (!target) {
      this.hideHoverOverlay();
      this.stopRepositioning();
      return;
    }

    this.startRepositioning();
    this.repositionHoverOverlay();
  }

  /** Flashes a temporary outline over `target` (scroll-to-bloc feedback). */
  flash(target: HTMLElement): void {
    const rect = getBlocClientRect(target);
    if (!rect) {
      return;
    }

    const overlay = document.createElement('div');
    applyOverlayBaseStyle(overlay);
    positionOverlay(overlay, rect);
    overlay.style.outline = `2px solid ${HIGHLIGHT_COLOR}`;
    overlay.style.outlineOffset = '2px';
    document.body.appendChild(overlay);

    if (typeof overlay.animate === 'function') {
      overlay.animate([{ background: FLASH_BACKGROUND }, { background: 'transparent' }], {
        duration: FLASH_DURATION_MS,
        easing: 'ease-out',
      });
    }

    window.setTimeout(() => overlay.remove(), FLASH_DURATION_MS);
  }

  /** Removes the hover overlay and its reposition listeners. */
  reset(): void {
    this.hoverTarget = null;
    this.stopRepositioning();
    this.hoverOverlay?.remove();
    this.hoverOverlay = null;
  }

  private repositionHoverOverlay(): void {
    const target = this.hoverTarget;
    const overlay = this.ensureHoverOverlay();
    const rect = target?.isConnected ? getBlocClientRect(target) : null;

    if (!rect) {
      overlay.style.display = 'none';
      return;
    }

    overlay.style.display = 'block';
    positionOverlay(overlay, rect);
  }

  private hideHoverOverlay(): void {
    if (this.hoverOverlay) {
      this.hoverOverlay.style.display = 'none';
    }
  }

  private ensureHoverOverlay(): HTMLElement {
    if (this.hoverOverlay) {
      return this.hoverOverlay;
    }

    const overlay = document.createElement('div');
    applyOverlayBaseStyle(overlay);
    overlay.style.background = HOVER_BACKGROUND;
    overlay.style.display = 'none';
    document.body.appendChild(overlay);
    this.hoverOverlay = overlay;
    return overlay;
  }

  private startRepositioning(): void {
    if (this.repositionUnlisteners.length > 0) {
      return;
    }

    const reposition = (): void => this.repositionHoverOverlay();
    // Scroll does not bubble but is observable in the capture phase; this also
    // covers nested scroll containers.
    document.addEventListener('scroll', reposition, REPOSITION_OPTIONS);
    window.addEventListener('resize', reposition);
    this.repositionUnlisteners.push(
      () => document.removeEventListener('scroll', reposition, REPOSITION_OPTIONS),
      () => window.removeEventListener('resize', reposition),
    );
  }

  private stopRepositioning(): void {
    while (this.repositionUnlisteners.length > 0) {
      this.repositionUnlisteners.pop()?.();
    }
  }
}

/**
 * Client rect of a bloc wrapper. `display: contents` wrappers generate no box,
 * so fall back to the union of their rendered contents via a Range.
 */
export function getBlocClientRect(element: HTMLElement): DOMRect | null {
  if (element.getClientRects().length > 0) {
    return element.getBoundingClientRect();
  }

  const range = document.createRange();
  range.selectNodeContents(element);
  const rect = range.getBoundingClientRect();
  range.detach();
  return rect.width > 0 || rect.height > 0 ? rect : null;
}

function applyOverlayBaseStyle(overlay: HTMLElement): void {
  overlay.style.position = 'fixed';
  overlay.style.pointerEvents = 'none';
  overlay.style.zIndex = '2147483000';
  overlay.style.borderRadius = '2px';
}

function positionOverlay(overlay: HTMLElement, rect: DOMRect): void {
  overlay.style.top = `${rect.top}px`;
  overlay.style.left = `${rect.left}px`;
  overlay.style.width = `${rect.width}px`;
  overlay.style.height = `${rect.height}px`;
}

const REPOSITION_OPTIONS: AddEventListenerOptions = { capture: true, passive: true };

const HIGHLIGHT_COLOR = 'var(--ui-color-primary-500, #6c8cff)';
const HOVER_BACKGROUND = `color-mix(in srgb, ${HIGHLIGHT_COLOR} 8%, transparent)`;
const FLASH_BACKGROUND = `color-mix(in srgb, ${HIGHLIGHT_COLOR} 35%, transparent)`;
const FLASH_DURATION_MS = 1600;
